import React, { useEffect, useState } from 'react'
import { Row, Col } from 'react-bootstrap';
import dateformat from "dateformat";
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useStoreState } from '../../index';
import { useSpring, animated } from '@react-spring/web';

import Thumbnail from 'src/components/thumbnail';
import Truncate from 'src/components/truncate';
import Tooltip from 'src/components/tooltip';
import InfiniteScroll from 'src/components/infiniteScroll';
import EmptyMessage from 'src/components/emptyMessage';
import EditAsset from 'src/pages/asset/components/edit';
import Slider from 'src/components/slider';
import Toast from 'src/components/toast';
import EditAssetThumbnail from 'src/pages/asset/components/edit/components/editAssetThumbnail';

import { Asset } from 'src/types/assets';
import { AssetsActionEnum } from 'src/types/contentMap';

import { FileService } from 'src/services/file.service';
import { fetchAssetAnalytics, invalidateAssetUpdations } from 'src/redux/actions/assets';

import _ from 'lodash';
import cx from 'classnames';
import styles from './styles.module.scss'
import { useContentMapAdoption } from 'src/redux/actions/adoption/contentMap';
import { useAssetAdoption } from 'src/redux/actions/adoption/asset';

const defaultAssetImg = '/images/defaultIcons/default_asset.jpg'


interface AssetListProps {
  handleFetchAssets: (page) => void,
  selectedAssets: Asset[],
  setSelectedAssets: (assets: Asset[]) => void,
  handleContentActions: (action: AssetsActionEnum) => void
}

const AssetList: React.FC<AssetListProps> = ({ handleFetchAssets, selectedAssets, setSelectedAssets, handleContentActions }) => {

  const dispatch = useDispatch();
  const {trackBulkAssetSelected, trackEditContentClicked, trackDeleteContentClicked } = useContentMapAdoption();
  const { trackAssetSelected } = useAssetAdoption();

  const { loading, succeeded: isAssetsFetched, items: assets, error } = useStoreState(`contentMap.assets.assetsList`, true);
  const assetUpdate = useSelector((state) => _.get(state, `asset.update[${_.get(selectedAssets[0], "id", "")}]`));

  const isAssetUpdated = _.get(assetUpdate, "updated", false);
  const isAssetUpdating = _.get(assetUpdate, "updating", false);

  const pagination = useSelector((state) => _.get(state, `contentMap.assets.assetsList.pagination`, {}));
  const [showEditAsset, setShowEditAsset] = useState<boolean>(false);
  const [showEditThumbnail, setShowEditThumbnail] = useState(false);


  useEffect(() => {
    if (_.size(selectedAssets) === 1) {
      if (!isAssetUpdating && isAssetUpdated) {
        setShowEditAsset(false);
        setSelectedAssets([]);
        dispatch(invalidateAssetUpdations({ id: selectedAssets[0].id }));
      }
    }
  }, [isAssetUpdating, isAssetUpdated])

  const loadMoreAssets = () => {

    const nextPage = _.get(pagination, "currentPage", 0) + 1;

    if (loading || nextPage >= _.get(pagination, "totalPages", 0)) {
      return;
    }

    handleFetchAssets(nextPage);
  }

  const handleSelectAll = () => {
    if (_.size(selectedAssets) === 0 || _.size(selectedAssets) !== _.size(assets)) {
      setSelectedAssets(assets);
      trackBulkAssetSelected();
    }
    else {
      setSelectedAssets([]);
    }
  }

  const handleAssetSelect = (asset) => {
    const selectedAssetIds = _.map(selectedAssets, 'id');
    const assetIndex = _.indexOf(selectedAssetIds, asset.id);

    if (assetIndex === -1) {
      setSelectedAssets([...selectedAssets, asset]);
    }
    else {
      setSelectedAssets(_.filter(selectedAssets, (selectedAsset) => selectedAsset.id !== asset.id));
    }
    const fileType = _.get(asset, "metadata", "");
    const assetType = FileService.getFileType(fileType) || '';

    // trackAssetSelected({
    //   sourceType: "",
    //   streamName: "",
    //   collectionName: "",
    //   sourceGroupName: "",
    //   assetType: assetType,
    //   sourceCta: CONTENT_MAP
    // })
  }

  const handleDeleteAssets = () => {
    let assetType = "";

    if (_.size(selectedAssets) === 1) {
      const selectedAsset = selectedAssets[0];
      assetType = _.get(selectedAsset, "metadata.extension", "");

      if (_.get(selectedAsset, "source", "") != null && !_.get(selectedAsset, "canDelete", false)) {

        toast(<Toast.Failure message={`This asset cannot be deleted as it is linked to a source`} />, {
          autoClose: 3000
        });

        setTimeout(() => {
          setSelectedAssets([]);
        }, 3000)
        return;
      }
      
    }
    handleContentActions(AssetsActionEnum.delete_assets)
    trackDeleteContentClicked({assetType: assetType})
  }

  const handleEditAssetsClick = () => {
    if (_.size(selectedAssets) === 1) {
      setShowEditAsset(true);
      const assetType = _.get(selectedAssets[0], "metadata.extension", "");
      trackEditContentClicked({assetType: assetType});
    }
    else {
      handleContentActions(AssetsActionEnum.update_assets)
      trackEditContentClicked({assetType: ""});
    }
  }

  const toggleEditThumbnail = () => {
    setShowEditThumbnail(!showEditThumbnail);
  };

  const selectedCountAnimation =
    useSpring({
      height: _.size(selectedAssets) > 0 ? "50px" : "0px",
      opacity: _.size(selectedAssets) > 0 ? 1 : 0,
      overflow: "hidden",
      config: { tension: 220, friction: 20 },
    });

  return (
    <div className={styles.assetListContainer}>
      {_.size(assets) > 0 && <div className={styles.headerContainer}>

        <animated.div style={selectedCountAnimation}>
          <div className={styles.assetsCount}>
            You've selected {_.size(selectedAssets)}<span className={styles.scroll}>(scroll to load more)</span> out of {_.get(pagination, "totalRecordsCount", 0)} assets
            <span onClick={handleEditAssetsClick} className={styles.actions}> <i className='paperflite-edit' /> Edit Content </span>
            <span onClick={handleDeleteAssets} className={styles.actions}> <i className='paperflite-delete' /> Delete Content </span>
          </div>
        </animated.div>

        <Row className={styles.innerContainer}>
          <i onClick={handleSelectAll}
            className={cx(
              _.size(selectedAssets) === _.size(assets) ? "fa-solid fa-check-circle" : 'paperflite-radio-button-unselected', {
              [styles.activeSelect]: _.size(selectedAssets) === _.size(assets)
            },
              styles.selectIcon)}></i>
          <Col xs={4}>Name</Col>
          <Col xs={2}>Details</Col>
          <Col className={styles.textLeft} xs={2}>Stream Name</Col>
          <Col className={styles.textCenter} xs={1}>Views</Col>
          <Col className={styles.textCenter} xs={1}>Shares</Col>
          <Col className={styles.textCenter} xs={1}>Downloads</Col>
          <Col className={styles.textCenter} xs={1}>Time spent</Col>
        </Row>
      </div>

      }

      <div className={styles.assetsContainer}>

        {loading && _.get(pagination, "currentPage", 0) === 0 && <RenderPlaceholderAssetCard />}

        {_.size(assets) > 0 && <InfiniteScroll className={styles.grid}
          loading={loading}
          hasMore={_.get(pagination, "hasMoreItems", false)}
          loadMore={() => loadMoreAssets()}
          loader={() => {
            return <RenderPlaceholderAssetCard />
          }}
        >
          {assets.map((asset: Asset) => {
            return <RenderAsset selectedAssets={selectedAssets} handleAssetSelect={handleAssetSelect} key={asset.id} asset={asset} />
          })}

        </InfiniteScroll>}

        {_.size(assets) === 0 && isAssetsFetched && <div className={styles.emptyMessageContainer}>
          <EmptyMessage
            width={400}
            image={'/images/assets/assetsEmptyMessage.svg'}
            heading="Looks like no assets match your filter. Houston, we have a problem.. Time to populate!"
          />
        </div>}

        {_.size(assets) === 0 && error && <div className={styles.emptyMessageContainer}>
          <EmptyMessage
            width={400}
            image={'/images/assets/assetsEmptyMessage.svg'}
            heading="Something went wrong"
          />
        </div>}

        <Slider.Dialog width="300" show={showEditAsset} onClose={() => setShowEditAsset(false)}>
          {_.size(selectedAssets) === 1 && <div className={styles.editAssetContainer}>
            <EditAsset asset={selectedAssets[0]} toggleEditAsset={() => setShowEditAsset(false)}
              hideEditVideoCaptions={true}
              toggleEditThumbnail={toggleEditThumbnail} />
          </div>
          }

        </Slider.Dialog>

        <Slider.Dialog
          width={"380px"}
          show={showEditThumbnail}
          onClose={toggleEditThumbnail}
        >
          {showEditThumbnail && (
            <EditAssetThumbnail
              asset={selectedAssets[0]}
              show={showEditThumbnail}
              onClose={toggleEditThumbnail}
            />
          )}
        </Slider.Dialog>

      </div>

    </div >
  )
}

const RenderPlaceholderAssetCard: React.FC<object> = () => {
  return (<div>
    {_.times(3, (index) => {
      return (
        <div key={index} className={cx(styles.placeholder)}>
          <div className={cx(styles.thumbnail, styles.block)}></div>
          <div className={styles.details}>
            <div className={cx(styles.name, styles.block)}></div>
            <p className={cx(styles.count, styles.block)}></p>
          </div>
        </div>
      );
    })}
  </div>)
}


const RenderAsset = (props) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { asset, selectedAssets } = props;
  const { analytics } = asset;
  const { views, shares, downloads, avgTimeSpent } = analytics || {};

  useEffect(() => {
    if (asset.id && !asset.analytics) {
      const request = {
        assetId: asset.id,
      };
      dispatch(fetchAssetAnalytics(request));
    }
  }, [])

  const timeSpentValue = (timeSpent: number) => {
    let timeSpentValue = `~${padNumber(timeSpent, 1)}s`;

    if (timeSpent >= 60) {
      timeSpentValue = `~${padNumber(Math.round(timeSpent / 60), 1)}m`;
    }

    return timeSpentValue;
  }

  const padNumber = (number = 0, pad) => {
    number = Math.ceil(number);

    const length = pad - String(number).length + 1;
    let padding = '';
    if (length > 0) {
      padding = Array(pad - String(number).length + 1).join('0');
    }

    return padding + number;
  }

  const calculateExpiry = (expiryDate) => {
    const formattedExpiryDate = expiryDate && new Date(expiryDate)
    const currentDate = new Date();
    const oneWeekLaterDate = currentDate.setDate(currentDate.getDate() + 7);
    if (formattedExpiryDate) {
      return formattedExpiryDate <= oneWeekLaterDate;
    }
    return false;
  }
  const params = useParams();
  const streamNames = _.get(asset, "streamNames", []);
  const streamNamesSize = _.size(streamNames);
  const fileType = FileService.getFileType(_.get(asset, "metadata"));
  const tags = _.map(_.get(asset, "tags", []), val => `#${val}`);
  const tagsString = _.join(tags, " ");
  const streamNamesString = _.join(streamNames, ", ");
  const isAssetSelected = _.indexOf(_.map(props.selectedAssets, 'id'), asset.id) !== -1;

  const isExpired = _.get(asset, "expiry", false);
  const expiryDate = _.get(asset, "expiryDate", null);
  const isExpiryInOneWeek = calculateExpiry(expiryDate);


  return (<div className={styles.asset}>
    <Row className={styles.row} onClick={() => {
      if (_.size(selectedAssets) === 0) {
        window.open(`/assets/${asset.id}?source=hub/content-map/${_.get(params,"id","")}`, '_blank');
      }
    }}>

      <Col xs={4}>
        <div className={styles.assetNameContainer} >
          <span className={styles.iconContainer} onClick={(e) => {
            e.stopPropagation();
            props.handleAssetSelect(props.asset)
          }
          }>
            <i
              className={cx(styles.assetSelectIcon, {
                [styles.activeSelect]: isAssetSelected
              }, isAssetSelected ? "fa-solid fa-check-circle" : 'paperflite-radio-button-unselected'
              )} />
            {!asset?.settings?.share &&
              <div className={styles.internalIcon}>
                <Tooltip content="Internal use only" placement="top">
                  <i className="paperflite-lock" />
                </Tooltip>
              </div>
            }
            <Thumbnail
              media={asset.icon}
              width={260}
              height={205}
              className={styles.thumbnail}
              key={_.get(asset,"icon.id","")}
              defaultImage={defaultAssetImg}
            />
            <div className={cx(styles.overlay, {
              [styles.activeOverlay]: isAssetSelected
            })} />
          </span>
          <div className={styles.content}>
            <span>
              <Tooltip content={asset.name} placement='top'>
                <span className={styles.nameInnerContainer}>
                  <Truncate width={250}>{asset.name}</Truncate>
                  {isExpired && <span className={styles.expired}>EXPIRED</span>}
                  {isExpiryInOneWeek && !isExpired && <span className={styles.expiry}>EXPIRING</span>}
                </span>

              </Tooltip>
              {_.size(tags) > 0 && <div className={styles.tags}>{tagsString}</div>}
            </span>
          </div>
        </div>

      </Col>
      <Col className={styles.alignLeft} xs={2}>
        <div className={styles.detailsContainer}>
          <div>{fileType} {fileType !== "INTERACTIVE" && <span className={styles.fileSize}> {FileService.formatSize(_.get(asset, "metadata.content_length"))} </span>}</div>
          <div className={styles.updatedDate}>Last updated {dateformat(_.get(asset, "modifiedDate"), "d mmm, yyyy")}{" "}</div>
        </div>
      </Col>
      <Col className={cx(styles.alignLeft, styles.streamNames)} xs={2}>

        <Tooltip content={streamNamesString} placement='top'>
          {_.get(streamNames, "[0]", "")}
          {streamNamesSize > 1 && <span className={styles.more}>
            {`& ${streamNamesSize - 1} more`}
          </span>}
        </Tooltip>
      </Col>
      <Col className={styles.alignCenter} xs={1}>{ views < 10 ? 0 : ""}{views}</Col>
      <Col className={styles.alignCenter} xs={1}>{ shares < 10 ? 0 : ""}{shares}</Col>
      <Col className={styles.alignCenter} xs={1}>{ downloads < 10 ? 0 : ""}{downloads}</Col>
      <Col className={styles.alignCenter} xs={1}>{timeSpentValue(avgTimeSpent)}</Col>
    </Row>
  </div>)
}

export default AssetList
